/* eslint-disable no-shadow */

import Vue from 'vue';
import ApiService from '@/services/api.service';
import { apiEndpoint } from '@/shared';
import { ValidationError } from '@/errors';

const endpoint = `${apiEndpoint}/projects`;

const state = {
  projects: null,
  pending: false,
  error: null,
  updatePending: false,
  updateError: null,
};

const getters = {
  loaded: (state) => !state.pending && state.projects != null,
  projects: (state, getters) => (getters.loaded ? state.projects : []),
  noProjects: (state, getters) => getters.loaded && !getters.projects.length,
};

const mutations = {
  clearErrors(state) {
    state.error = null;
    state.updateError = null;
  },
  beforeLoading(state, initial = false) {
    if (initial) {
      state.projects = null;
    }
    state.error = null;
    state.pending = true;
  },
  beforeUpdate(state) {
    state.updateError = null;
    state.updatePending = true;
  },
  error(state, error) {
    state.pending = false;
    state.error = error;
  },
  data(state, { projects }) {
    state.pending = false;
    state.projects = projects;
  },
  update(state, error = null) {
    state.updatePending = false;
    state.updateError = error;
  },
  created(state, project) {
    state.projects.push(JSON.parse(JSON.stringify(project)));
    state.updatePending = false;
    state.updateError = null;
  },
  modified(state, project) {
    const idx = state.projects.findIndex((p) => p.id === project.id);
    if (idx !== -1) {
      Vue.set(state.projects, idx, JSON.parse(JSON.stringify(project)));
    }
    state.updatePending = false;
    state.updateError = null;
  },
  deleted(state, pid) {
    const idx = state.projects.findIndex((project) => project.pid === pid);
    if (idx !== -1) {
      state.projects.splice(idx, 1);
    }
    state.updatePending = false;
    state.updateError = null;
  },
};

function handleApiError(commit, error) {
  const { status, data } = error.response;
  let err = error;
  switch (status) {
    case 422:
      if (data.errors) {
        data.message = data.errors[Object.keys(data.errors)[0]];
      }
      err = new ValidationError(status, data.message, data.errors);
      break;
    default:
      break;
  }
  return err;
}

const actions = {
  async fetchData({ commit }, forced = false) {
    commit('beforeLoading', forced);
    try {
      const result = await ApiService.get(`${endpoint}`);
      const projects = result.data.data;
      commit('data', { projects });
    } catch (error) {
      commit('error', error);
    }
  },
  async createProject({ commit }, project) {
    commit('beforeUpdate');
    try {
      const { data } = await ApiService.post(`${endpoint}`, { ...project });
      commit('created', data);
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async updateProject({ commit }, project) {
    commit('beforeUpdate');
    try {
      await ApiService.put(`${endpoint}/${project.id}`, { ...project });
      commit('modified', { ...project });
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
  async deleteProject({ commit }, pid) {
    commit('beforeUpdate');
    try {
      await ApiService.delete(`${endpoint}/${pid}`);
      commit('deleted', pid);
    } catch (error) {
      commit('update', error);
      throw handleApiError(commit, error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
